import { 
  Card, 
  CardHeader, 
  CardBody, 
  Heading, 
  Text, 
  Box, 
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { TimeRangeButton } from '../TimeRangeButton';
import ReactMarkdown from 'react-markdown';

interface TimeToValueTimestamp {
  start: number | null;
  end: number | null;
  context: string | null;
}

interface EnhancedSummaryProps {
  summary: string;
  keyValueProp?: string;
  shouldListen?: boolean;
  timeToValueTimestamps?: TimeToValueTimestamp;
  mood?: string;
}

export const EnhancedSummary = ({ 
  summary,
  keyValueProp,
  shouldListen,
  timeToValueTimestamps,
  mood
}: EnhancedSummaryProps) => {
  if (!summary && !keyValueProp && shouldListen === undefined && !timeToValueTimestamps && !mood) return null;
  
  // Check if timeToValueTimestamps has valid values
  const hasValidTimeToValue = timeToValueTimestamps && 
    timeToValueTimestamps.start !== null && 
    timeToValueTimestamps.end !== null && 
    timeToValueTimestamps.context !== null;

  // Colors for the summary section
  const summaryBgColor = useColorModeValue('blue.50', 'blue.900');
  const cardBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('blue.200', 'blue.700');

  return (
    <Card 
      boxShadow="md" 
      borderRadius="lg" 
      bg={cardBgColor}
      borderWidth="1px"
      borderColor={borderColor}
    >
      <CardHeader>
        <Heading 
          size="md" 
          fontFamily="'Poppins', sans-serif" 
          letterSpacing="tight"
          color={useColorModeValue('gray.700', 'white')}
        >
          Summary
        </Heading>
      </CardHeader>
      <CardBody>
        <VStack spacing={5} align="stretch">
          {summary && (
            <Box 
              className="markdown-content" 
              bg={summaryBgColor}
              p={4}
              borderRadius="md"
              borderLeft="4px solid"
              borderColor="blue.400"
              fontFamily="'Inter', sans-serif"
              fontSize="md"
              lineHeight="1.7"
              boxShadow="sm"
            >
              <ReactMarkdown>{summary}</ReactMarkdown>
            </Box>
          )}
          
          {keyValueProp && (
            <Box mt={2}>
              <Text 
                fontWeight="bold" 
                mb={2}
                fontFamily="'Poppins', sans-serif"
                fontSize="sm"
                textTransform="uppercase"
                color={useColorModeValue('gray.600', 'gray.300')}
              >
                Key Value Proposition
              </Text>
              <Box 
                className="markdown-content"
                fontFamily="'Inter', sans-serif"
                fontSize="md"
                lineHeight="1.6"
              >
                <ReactMarkdown>{keyValueProp}</ReactMarkdown>
              </Box>
            </Box>
          )}
          
          {hasValidTimeToValue && (
            <Box mt={2}>
              <Text 
                fontWeight="bold" 
                mb={2}
                fontFamily="'Poppins', sans-serif"
                fontSize="sm"
                textTransform="uppercase"
                color={useColorModeValue('gray.600', 'gray.300')}
              >
                Time to Value
              </Text>
              <TimeRangeButton
                start={timeToValueTimestamps.start as number}
                end={timeToValueTimestamps.end as number}
                description={timeToValueTimestamps.context as string}
              />
            </Box>
          )}

          {mood && (
            <Box mt={2}>
              <Text 
                fontWeight="bold" 
                mb={1}
                fontFamily="'Poppins', sans-serif"
                fontSize="sm"
                textTransform="uppercase"
                color={useColorModeValue('gray.600', 'gray.300')}
              >
                Mood
              </Text>
              <Text fontFamily="'Inter', sans-serif">{mood}</Text>
            </Box>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
}; 